import { processPointData, b64toBlob } from '../PointTable/utils';

export { b64toBlob };

const RAD_TO_DEG = 180 / Math.PI;

const BAND_HEIGHT_M = 400;
const ALTITUDE_TOP_M = 12000;
const ALTITUDE_STEP_M = 100;

const BARB_ALTITUDES_M = [500, 1500, 3000, 4500, 6000, 9000, 12000];

// Pixel height of the windgram cross-section row. Shared between WindgramChart
// (renders the SVG at this viewBox height) and PointTableV2 (sets the row height
// + positions altitude labels at the same y-coordinates).
export const WINDGRAM_HEIGHT = 200;

// Adaptive altitude scaling matching Skysight's Z8() chart function. The y-axis
// is piecewise-linear: the lower zone (terrain → max PBL height + 300m) takes
// 75% of pixel height, the upper zone (above PBL → data top) takes the rest.
// This compresses uninteresting upper atmosphere while spreading the soaring layer.
// Skysight's windgram instance uses 0.60 (the bundle's defaultRatio of 0.75
// is overridden for this chart). With 0.60, the lower zone takes 60% of pixel
// height, upper takes 40% — giving more vertical room to the high-altitude
// barbs/wind shear band.
const SPLIT_RATIO = 0.60;

export const buildAltitudeScale = ({ base, pblhMax, dataTop, height = WINDGRAM_HEIGHT }) => {
    const v = Math.max(0, base);
    const g = Math.max(v + 1, dataTop);
    const rawSplit = Math.max(v + 1000, Math.min(g, pblhMax + 300));
    const useSplit = rawSplit < g;
    const splitAlt = useSplit ? rawSplit : g;
    const h = height * (1 - SPLIT_RATIO); // pixel y at the split point (closer to top)

    const altToY = (alt) => {
        if (alt <= v) return height;
        if (alt >= g) return 0;
        if (!useSplit) return height - ((alt - v) / (g - v)) * height;
        if (alt <= splitAlt) return height - ((alt - v) / (splitAlt - v)) * (height - h);
        return h - ((alt - splitAlt) / (g - splitAlt)) * h;
    };
    const yToAlt = (y) => {
        if (y >= height) return v;
        if (y <= 0) return g;
        if (!useSplit) return v + ((height - y) / height) * (g - v);
        if (y >= h) return v + ((height - y) / (height - h)) * (splitAlt - v);
        return splitAlt + ((h - y) / h) * (g - splitAlt);
    };

    // Tick density: aim for ~4 ticks (sparse, like Skysight). 200/50 = 4.
    const nTicks = Math.max(4, Math.min(7, Math.round(height / 50)));
    const rawTicks = [];
    for (let i = 0; i < nTicks; i++) {
        rawTicks.push(yToAlt((height / (nTicks - 1)) * i));
    }
    // Round each tick to nearest 100m. Use Math.floor of base (rounded down to
    // 100m) as the lower bound so the bottom tick isn't filtered out when base
    // rounds down (e.g. base=1840 → tick 1800 must still be accepted).
    const vRoundedDown = Math.floor(v / 100) * 100;
    const ticks = Array.from(new Set(rawTicks.map(a => Math.round(a / 100) * 100)))
        .filter(a => a >= vRoundedDown && a <= g)
        .sort((a, b) => a - b);

    return { altToY, yToAlt, splitAlt, ticks };
};

export const scaleFromWindgram = (windgram, height = WINDGRAM_HEIGHT) => {
    if (!windgram || !windgram.timesteps || windgram.timesteps.length === 0) return null;
    const firstCol = windgram.timesteps[0].column;
    const dataTop = firstCol[firstCol.length - 1].altitude;
    const pblhMax = Math.max(...windgram.timesteps.map(ts => parseFloat(ts.pblh) || 0));
    return buildAltitudeScale({ base: windgram.base, pblhMax, dataTop, height });
};

const collapseToBands = (timesteps, bandHeightM) => {
    const slicesPerBand = bandHeightM / ALTITUDE_STEP_M;
    const numBands = ALTITUDE_TOP_M / bandHeightM;

    const bands = [];
    for (let b = 0; b < numBands; b++) {
        const altLow = b * bandHeightM;
        const altHigh = altLow + bandHeightM;
        const row = timesteps.map(ts => {
            let cldfraSum = 0;
            let count = 0;
            for (let s = b * slicesPerBand; s < (b + 1) * slicesPerBand && s < ts.column.length; s++) {
                cldfraSum += ts.column[s].cldfra;
                count++;
            }
            return {
                cldfra: count > 0 ? cldfraSum / count : 0,
                altLow,
                altHigh,
            };
        });
        bands.push(row);
    }
    return bands;
};

export const processWindgram = (windgram, hwcrit, zsfclcl) => {
    if (!windgram || !windgram.data || !Array.isArray(windgram.data) || windgram.data.length === 0) {
        return null;
    }

    const base = parseFloat(windgram.base);

    const timesteps = windgram.data.map(step => ({
        dt: step.dt,
        cu_oktas: step.cu_oktas,
        accrain: step.accrain,
        cape: step.cape,
        cfrach: parseFloat(step.cfrach) || 0,
        cfracm: parseFloat(step.cfracm) || 0,
        cfracl: parseFloat(step.cfracl) || 0,
        zsfclcl: step.zsfclcl,
        zsfclcldif: step.zsfclcldif,
        pblh: step.pblh,
        column: step.data.map(slice => {
            const u = parseFloat(slice.u);
            const v = parseFloat(slice.v);
            return {
                altitude: slice.altitude,
                u,
                v,
                cldfra: parseFloat(slice.cldfra),
                rh: parseFloat(slice.rh),
                temp: parseFloat(slice.temp),
                vert: parseFloat(slice.vert),
                ti: parseFloat(slice.ti),
                speed: Math.hypot(u, v),
                // Meteorological convention: direction the wind blows FROM, measured
                // clockwise from north. WindBarb rotates by this value with the stem
                // pointing toward the source of the wind.
                dirDeg: (Math.atan2(-u, -v) * RAD_TO_DEG + 360) % 360,
            };
        }),
    }));

    // Skysight's bundle shows the BLACK "thermal top" line is driven by per-timestep
    // pblh (planetary boundary layer height) from the windgram block — NOT the
    // top-level hwcrit (Height of critical updraft strength). zsfclcl drives the
    // pink condensation line as before.
    const thermalTopLine = timesteps.map((ts, i) => [i, Math.round(parseFloat(ts.pblh) || 0)]);
    const condensationLine = timesteps.map((ts, i) => [i, Math.round(parseFloat(ts.zsfclcl) || 0)]);

    const heatmapBands = collapseToBands(timesteps, BAND_HEIGHT_M);

    return {
        base,
        timesteps,
        thermalTopLine,
        condensationLine,
        heatmapBands,
        barbAltitudes: BARB_ALTITUDES_M,
    };
};

export const processPointDataV2 = (data, date, model, times, currentLanguage) => {
    const base = processPointData(data, date, model, times, currentLanguage);
    return {
        ...base,
        windgram: processWindgram(data.windgram, data.hwcrit, data.zsfclcl),
        da: Array.isArray(data.da) ? data.da.map(Math.round) : [],
        // ICON doesn't compute Potential Flight Distance — the API returns all
        // zeros (verified 2026-06-11), which would read as "0 km flyable".
        // Mask it so the row renders as "—" instead.
        potfd: model === 'ICON' || !Array.isArray(data.potfd) ? [] : data.potfd.map(Math.round),
        ap: Array.isArray(data.ap) ? data.ap : [],
    };
};
